import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { getCompleteEntityData, getMapDataArrayRaw } from 'src/redux/selectors'
import { getCoinData, getCoinDetailTabData } from 'src/redux/thunks'
import { MainLayout } from 'src/layouts'
import { Tabs } from 'src/components/Tabs'
import { Breadcrumbs } from 'src/components/Breadcrumbs'
import { ENTITY, COIN_DETAIL_TABS, MAIN_SITE_IMAGE } from 'src/consts'
import useWidth from 'src/hooks/useWidth'
import { Loader } from 'src/components/Loader'
import {
  AboutBlock,
  InfoTable,
  MarketsTab,
  RelatedNews,
  TopInfoBlock,
  WidgetsCustomizer,
} from './components'
import styles from './styles.module.scss'
import Chart from 'public/assets/icons/icon-chart.svg'
import Markets from 'public/assets/icons/icon-markets.svg'
import News from 'public/assets/icons/icon-news.svg'
import Widgets from 'public/assets/icons/icon-widget.svg'
import Union from 'public/assets/icons/icon-union.svg'
import { ChartsTab } from './components/ChartsTab'
import ErrorPage from 'src/pages/ErrorPage'
import { RubicWidget } from './components/RubicWidget'
import { useAppIntl } from 'src/i18n'
import { WEBSITE } from 'src/consts'

const SkyscraperBanner = dynamic(
  () => import('src/components/Banners/Skyscraper'),
  { ssr: false }
)

export const CoinDetails = () => {
  const intl = useAppIntl()
  const router = useRouter()
  const coinData = useSelector(getCompleteEntityData)
  const mapDataArray = useSelector(getMapDataArrayRaw)
  const dispatch = useDispatch()
  const { isMobile } = useWidth()

  const [loading, setLoading] = useState(true) // New loading state

  useEffect(() => {
    if (router.isReady) {
      setLoading(true)
      dispatch(getCoinData({ slug: router.query.slug })).then(() => {
        setLoading(false)
      })
    }
  }, [dispatch, router.isReady, router.query.slug])

  useEffect(() => {
    if (coinData?.slug) {
      dispatch(getCoinDetailTabData({ tab: 'chart', slug: coinData.slug }))
      dispatch(getCoinDetailTabData({ tab: 'markets', slug: coinData.slug }))
      dispatch(getCoinDetailTabData({ tab: 'news', slug: coinData.slug }))
      dispatch(getCoinDetailTabData({ tab: 'widgets', slug: coinData.slug }))
      dispatch(getCoinDetailTabData({ tab: 'swap', slug: coinData.slug }))

      const intervalData = setInterval(() => {
        dispatch(getCoinData({ slug: coinData.slug, isRefresh: true }))
      }, 10000)

      return () => clearInterval(intervalData)
    }
  }, [dispatch, coinData?.slug])

  // Show Loader if data is still loading
  if (loading) return <Loader isActive />

  if (!coinData)
    return (
      <ErrorPage
        code={404}
        description='Sorry, the page you are looking for has not been found. Try returning to the Homepage.'
        title='Page not found'
      />
    )

  const tabs = [
    {
      id: COIN_DETAIL_TABS.chart,
      icon: <Chart />,
      title: intl.t(`coinDetails.tabs.chart`),
      content: <ChartsTab coin={coinData} />,
    },
    {
      id: COIN_DETAIL_TABS.markets,
      icon: <Markets />,
      title: intl.t(`coinDetails.tabs.markets`),
      content: (
        <MarketsTab
          name={coinData.name}
          slug={coinData.slug}
          mapDataArray={mapDataArray}
        />
      ),
    },
    {
      id: COIN_DETAIL_TABS.news,
      icon: <News />,
      title: intl.t(`coinDetails.tabs.news`),
      content: <RelatedNews coinData={coinData} />,
    },
    {
      id: COIN_DETAIL_TABS.widgets,
      icon: <Widgets />,
      title: intl.t(`coinDetails.tabs.widgets`),
      content: <WidgetsCustomizer coinData={coinData} />,
      hideOnMobile: true,
    },
    {
      id: COIN_DETAIL_TABS.swap,
      icon: <Union />,
      title: intl.t(`coinDetails.tabs.swap`),
      content: <RubicWidget />,
    },
  ]

  return (
    <MainLayout
      title={coinData.name}
      description={coinData.name}
      uri={`/coin/${coinData.slug}`}
      scrollToTop
      fixedHeader
    >
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          items={[
            { breadcrumb: 'Cryptocurrencies', href: '/' },
            { breadcrumb: 'Coins', href: '/coin' },
          ]}
          lastItem={coinData.name}
          code={coinData.code}
        />
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.mainContent}>
          <TopInfoBlock data={coinData} />
          <Tabs data={tabs} initialTab={COIN_DETAIL_TABS.chart} />
          <div className={styles.extraInfoBlock}>
            <div className={styles.descriptionBlock}>
              <AboutBlock data={coinData} />
            </div>
            <InfoTable data={coinData} />
          </div>
        </div>
        {!isMobile && (
          <div className={styles.sidebar}>
            <SkyscraperBanner />
          </div>
        )}
      </div>
    </MainLayout>
  )
}

export default CoinDetails
